import * as React from 'react';

import { CATEGORY_GLOBAL_NAV } from 'utils/analytics/eventTypesConstants';
import { EventCustomAttributes } from 'utils/analytics/eventTypeConstantsAmplitude';
import {
  SearchBarOption,
  SearchBarOptionOrSectionDivider,
  SearchNavEntityTypes,
} from 'utils/searchBar';
import { assertNever } from '@tock/shared/utils/typeUtils';
import { consumer, core } from '@tock/coretypes';
import { useAmplitudeSendEvent } from 'utils/analytics/AnalyticsContext';
import SearchBarCombined from 'components/nav-search/SearchBarCombined';

export interface CoreSearchNavProps {
  currentSearch: string;
  setCurrentSearch(value: string): void;
  isDialogOpen: boolean;
  onClose(): void;
  metroArea?: consumer.IMetroArea;
}

type Props = {
  allMetroAreas: core.IMetroArea[];
  currentMetroArea?: core.MetroArea;
  onSelectMetroArea?(metro: core.MetroArea): void;
  onSearch(searchText: string): Promise<ProtoResponse<consumer.NavSuggestResponse>>;
  searchResults: SearchBarOptionOrSectionDivider[];
  clearSearchResults(): void;
} & CoreSearchNavProps;

const sendAnalyticsEvent = (eventAction: string, eventValue: string) => {
  window.analytics.sendEvent(CATEGORY_GLOBAL_NAV, eventAction, eventValue);
};

export const getHandleSelectEntryReservations = (
  amplitudeSendEvent: (action: string, data: EventCustomAttributes) => void,
  currentSearch: string,
  currentMetroAreaName?: string
) => {
  const sendAmplitudeNavEvent = (data: EventCustomAttributes) => {
    amplitudeSendEvent('Click nav omnisearch entry', data);
  };

  return (entry: SearchBarOption, listIndex: number) => {
    switch (entry.category) {
      case SearchNavEntityTypes.BUSINESS:
        sendAnalyticsEvent('Business search result selected', entry.primaryText);
        sendAmplitudeNavEvent({
          navCategory: entry.category,
          navEntry: entry.primaryText,
          query: currentSearch,
          businessName: entry.business.name || undefined,
          listIndex,
        });
        break;
      case SearchNavEntityTypes.CITY: {
        sendAnalyticsEvent('City search result selected', entry.primaryText);
        sendAmplitudeNavEvent({
          navCategory: entry.category,
          navEntry: entry.primaryText,
          query: currentSearch,
          listIndex,
        });
        break;
      }
      case SearchNavEntityTypes.CUISINE: {
        sendAnalyticsEvent('Nav cuisine search selected', entry.primaryText);
        sendAmplitudeNavEvent({
          navCategory: entry.category,
          navEntry: entry.primaryText,
          query: currentSearch,
          listIndex,
        });
        break;
      }
      case SearchNavEntityTypes.TEXT: {
        sendAnalyticsEvent('Text search selected', `${currentSearch}:${currentMetroAreaName}`);
        sendAmplitudeNavEvent({
          navCategory: entry.category,
          query: currentSearch,
          listIndex,
        });
        break;
      }
      case SearchNavEntityTypes.QUERY:
        sendAmplitudeNavEvent({
          navCategory: entry.subCategory,
          navEntry: entry.primaryText,
          listIndex,
        });
        break;
      case SearchNavEntityTypes.SHOP_ITEM:
      case SearchNavEntityTypes.SHOP_BRAND:
      case SearchNavEntityTypes.WINE_STYLE:
      case SearchNavEntityTypes.WINE_VARIETAL:
      case SearchNavEntityTypes.WINE_REGION:
        // n/a for reservations
        break;
      case SearchNavEntityTypes.HEADING:
        break;
      default:
        assertNever(entry);
    }
  };
};

const SearchNav: React.FunctionComponent<Props> = ({
  clearSearchResults,
  currentSearch,
  searchResults,
  setCurrentSearch,
  onSearch,
  currentMetroArea,
  onSelectMetroArea,
  allMetroAreas,
  isDialogOpen,
  onClose,
  metroArea,
}) => {
  const amplitudeSendEvent = useAmplitudeSendEvent();
  const handleSelectEntry = (entry: SearchBarOption, listIndex: number) => {
    getHandleSelectEntryReservations(
      amplitudeSendEvent,
      currentSearch,
      currentMetroArea?.name
    )(entry, listIndex);

    if (entry.category === SearchNavEntityTypes.CITY) {
      const selectedMetro = allMetroAreas.find((metroArea) => entry.primaryText === metroArea.name);
      if (selectedMetro && onSelectMetroArea) {
        onSelectMetroArea(selectedMetro as core.MetroArea);
      }
    }

    onClose();
  };

  return (
    <SearchBarCombined
      clearSearchResults={clearSearchResults}
      currentSearch={currentSearch}
      onClose={onClose}
      onSelectEntry={handleSelectEntry}
      placeholder="Find a business, cuisine, or city"
      results={searchResults}
      setCurrentSearch={setCurrentSearch}
      onSearch={onSearch}
      isDialogOpen={isDialogOpen}
      metroArea={metroArea}
    />
  );
};

export default SearchNav;
